/**
 * Document Category Detector
 *
 * Auto-detects document category from filename patterns.
 * Used in Quick Create for auto-categorization of uploaded documents.
 */

export interface CategoryDetectionResult {
  category: string | null;
  categoryLabel: string | null;
  confidence: number;
}

// Document categories matching COMPLIANCE_CATEGORIES in JobDocuments.tsx
export const DOCUMENT_CATEGORIES = [
  { value: 'contractor_license', label: 'Contractor License' },
  { value: 'insurance_coi', label: 'Certificate of Insurance' },
  { value: 'load_calculation', label: 'Load Calculation (Manual J/D/S)' },
  { value: 'equipment_specs', label: 'Equipment Specifications' },
  { value: 'site_plan', label: 'Site Plan' },
  { value: 'riser_diagram', label: 'Riser Diagram' },
  { value: 'single_line', label: 'Single Line Diagram' },
  { value: 'energy_calc', label: 'Energy Calculation' },
  { value: 'product_approval', label: 'Product Approval (NOA/FBC)' },
  { value: 'sealed_drawings', label: 'Sealed Drawings' },
  { value: 'owner_auth', label: 'Owner Authorization' },
  { value: 'building_plans', label: 'Building Plans' },
  { value: 'survey', label: 'Property Survey' },
  { value: 'application_form', label: 'Permit Application Form' },
] as const;

// Pattern matching rules for auto-detection
// Each rule has patterns to match and the resulting category
const DETECTION_RULES: Array<{
  patterns: RegExp[];
  category: string;
  confidence: number;
}> = [
  // Contractor License - high confidence patterns
  {
    patterns: [
      /licen[sc]e/i,
      /contractor.*cert/i,
      /cert.*contractor/i,
      /state.*license/i,
      /professional.*license/i,
      /journeyman/i,
      /master.*electrician/i,
      /master.*plumber/i,
    ],
    category: 'contractor_license',
    confidence: 0.9,
  },

  // Certificate of Insurance
  {
    patterns: [
      /coi\b/i,
      /insurance/i,
      /cert.*insurance/i,
      /liability/i,
      /workers.*comp/i,
      /accord/i,
      /coverage/i,
    ],
    category: 'insurance_coi',
    confidence: 0.9,
  },

  // Load Calculation
  {
    patterns: [
      /load.*calc/i,
      /manual[\s_-]*j/i,
      /manual[\s_-]*d/i,
      /manual[\s_-]*s/i,
      /hvac.*calc/i,
      /heat.*loss/i,
      /cooling.*calc/i,
      /btuh/i,
      /acca/i,
    ],
    category: 'load_calculation',
    confidence: 0.85,
  },

  // Equipment Specifications
  {
    patterns: [
      /equipment.*spec/i,
      /spec.*sheet/i,
      /data.*sheet/i,
      /submittal/i,
      /product.*spec/i,
      /tech.*spec/i,
      /mechanical.*spec/i,
    ],
    category: 'equipment_specs',
    confidence: 0.8,
  },

  // Site Plan
  {
    patterns: [
      /site.*plan/i,
      /plot.*plan/i,
      /site.*layout/i,
      /property.*layout/i,
    ],
    category: 'site_plan',
    confidence: 0.85,
  },

  // Riser Diagram
  {
    patterns: [
      /riser/i,
      /electrical.*diagram/i,
      /panel.*schedule/i,
    ],
    category: 'riser_diagram',
    confidence: 0.8,
  },

  // Single Line Diagram
  {
    patterns: [
      /single[\s_-]*line/i,
      /one[\s_-]*line/i,
      /sld\b/i,
      /electrical.*schematic/i,
    ],
    category: 'single_line',
    confidence: 0.85,
  },

  // Energy Calculation
  {
    patterns: [
      /energy.*calc/i,
      /rescheck/i,
      /comcheck/i,
      /energy.*code/i,
      /energy.*compliance/i,
      /envelope/i,
    ],
    category: 'energy_calc',
    confidence: 0.85,
  },

  // Product Approval (NOA/FBC)
  {
    patterns: [
      /noa\b/i,
      /notice.*accept/i,
      /fbc\b/i,
      /florida.*building.*code/i,
      /product.*approval/i,
      /miami.*dade/i,
      /high.*velocity/i,
    ],
    category: 'product_approval',
    confidence: 0.9,
  },

  // Sealed Drawings
  {
    patterns: [
      /sealed/i,
      /engineer.*stamp/i,
      /architect.*stamp/i,
      /pe.*stamp/i,
      /stamped/i,
      /engineered/i,
    ],
    category: 'sealed_drawings',
    confidence: 0.75,
  },

  // Owner Authorization
  {
    patterns: [
      /owner.*auth/i,
      /authorization/i,
      /property.*owner/i,
      /consent/i,
      /affidavit/i,
      /notarized/i,
    ],
    category: 'owner_auth',
    confidence: 0.8,
  },

  // Building Plans
  {
    patterns: [
      /floor.*plan/i,
      /building.*plan/i,
      /construction.*drawing/i,
      /blueprint/i,
      /elevation/i,
      /foundation.*plan/i,
      /framing.*plan/i,
    ],
    category: 'building_plans',
    confidence: 0.75,
  },

  // Survey
  {
    patterns: [
      /survey/i,
      /boundary/i,
      /topo/i,
      /topograph/i,
      /plat\b/i,
    ],
    category: 'survey',
    confidence: 0.85,
  },

  // Permit Application Form
  {
    patterns: [
      /application/i,
      /permit.*form/i,
      /intake.*form/i,
    ],
    category: 'application_form',
    confidence: 0.7,
  },
];

/**
 * Detect document category from filename
 *
 * @param filename - The name of the file to analyze
 * @returns Detection result with category, label, and confidence
 */
export function detectDocumentCategory(filename: string): CategoryDetectionResult {
  const normalizedName = filename.toLowerCase().replace(/[_-]/g, ' ');

  // Try each detection rule
  for (const rule of DETECTION_RULES) {
    for (const pattern of rule.patterns) {
      if (pattern.test(normalizedName)) {
        const categoryInfo = DOCUMENT_CATEGORIES.find((c) => c.value === rule.category);
        return {
          category: rule.category,
          categoryLabel: categoryInfo?.label || rule.category,
          confidence: rule.confidence,
        };
      }
    }
  }

  // No match found
  return {
    category: null,
    categoryLabel: null,
    confidence: 0,
  };
}

/**
 * Get category label from category value
 */
export function getCategoryLabel(categoryValue: string): string {
  const category = DOCUMENT_CATEGORIES.find((c) => c.value === categoryValue);
  return category?.label || categoryValue;
}

/**
 * Get all available document categories
 */
export function getDocumentCategories() {
  return DOCUMENT_CATEGORIES;
}
